import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, UserRound, MapPin, Stethoscope } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Appointment } from '../types';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { V2 } from '../v2/shared';
import { clinicTime, clinicDateKey, dateFromKey } from '../utils/clinicTime';

const STATUS: Record<string, { label: string; hint: string; cls: string }> = {
  pending: { label: 'Esperando confirmación', hint: 'Reservamos tu horario; lo confirmamos en menos de 24 horas hábiles.', cls: 'chip-wait' },
  confirmed: { label: 'Confirmado', hint: '', cls: 'chip-ok' },
  completed: { label: 'Realizado', hint: '', cls: 'chip-done' },
  cancelled: { label: 'Cancelado', hint: '', cls: 'chip-off' },
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const AppointmentCard: React.FC<{ a: Appointment }> = ({ a }) => {
  const start = dateFromKey(clinicDateKey(a.start_time));
  const hhmm = clinicTime(a.start_time);
  const st = STATUS[a.status] || { label: a.status, hint: '', cls: 'chip-done' };
  return (
    <article className="appt">
      <div className="appt-date" aria-hidden="true">
        <b>{format(start, 'd')}</b><span>{format(start, 'MMM', { locale: es })}</span>
      </div>
      <div className="appt-body">
        <div className="appt-top">
          <h3>{a.specialty || 'Consulta'}</h3>
          <span className={`chip ${st.cls}`}>{st.label}</span>
        </div>
        <p className="appt-line"><CalendarDays size={16} /> {cap(format(start, "EEEE d 'de' MMMM", { locale: es }))}</p>
        <p className="appt-line"><Clock size={16} /> {hhmm} hs</p>
        <p className="appt-line"><UserRound size={16} /> {a.professional_name || 'Profesional a confirmar'}</p>
        {a.room_name && <p className="appt-line"><MapPin size={16} /> {a.room_name}</p>}
        {st.hint && <p className="appt-hint">{st.hint}</p>}
      </div>
    </article>
  );
};

const PatientDashboard: React.FC = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[] | null>(null);

  useEffect(() => {
    api.get('/appointments')
      .then(r => setAppointments(r.data))
      .catch(() => { toast.error('No pudimos cargar tus turnos'); setAppointments([]); });
  }, []);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const list = appointments || [];
    const isUpcoming = (a: Appointment) => new Date(a.start_time).getTime() >= now && (a.status === 'pending' || a.status === 'confirmed');
    return {
      upcoming: list.filter(isUpcoming).sort((a, b) => +new Date(a.start_time) - +new Date(b.start_time)),
      past: list.filter(a => !isUpcoming(a)).sort((a, b) => +new Date(b.start_time) - +new Date(a.start_time)),
    };
  }, [appointments]);

  return (
    <V2>
      <section className="pg">
        <div className="wrap">
          <h1>Mis turnos{user ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p>Tus próximas consultas y tu historial.</p>
        </div>
      </section>

      <main className="wrap" style={{ padding: '28px 20px 72px', maxWidth: 820 }}>
        {appointments === null && <div className="skel" style={{ height: 160 }} />}

        {appointments !== null && appointments.length === 0 && (
          <div className="panel" style={{ textAlign: 'center', padding: '44px 24px' }}>
            <div className="avatar" style={{ width: 64, height: 64, margin: '0 auto 16px' }}><Stethoscope size={28} /></div>
            <h3>Todavía no tenés turnos</h3>
            <p className="hint" style={{ margin: '6px 0 20px' }}>Cuando pidas uno, lo vas a ver acá con su estado.</p>
            <Link to="/#especialidades" className="btn2 btn2-cta">Pedir un turno</Link>
          </div>
        )}

        {upcoming.length > 0 && (<section aria-labelledby="up">
          <h2 id="up" className="sec-title">Próximos</h2>
          <div className="appt-list">{upcoming.map(a => <AppointmentCard key={a.id} a={a} />)}</div>
        </section>)}

        {past.length > 0 && (<section aria-labelledby="past" style={{ marginTop: 36 }}>
          <h2 id="past" className="sec-title">Historial</h2>
          <div className="appt-list">{past.map(a => <AppointmentCard key={a.id} a={a} />)}</div>
        </section>)}
      </main>
    </V2>
  );
};

export default PatientDashboard;
